// Central config for the PPE Pricing tool's calls to the unified pricing engine.
//
// It calls the authenticated web/go gateway (handlePricing) through a same-origin
// relative path, exactly like the legacy Rates tool calls /data/lopriceengine:
// no CORS, and customerid is derived from the session token server-side. The
// gateway forwards to cost_calculator's /api/v1/pricing.
const PPE_ENDPOINT = "/data/pricing";

// Keep browser-side validation aligned with the public PPE request contract.
export const CREDIT_SCORE_MIN = 500;
export const CREDIT_SCORE_MAX = 850;

// Form options that wait on a calculator work package of
// LenderDocsProcessors/docs/plans/ppe-simplify-epps.md. Each stays off until
// that package is in production, because the page must never send a field the
// production calculator does not know (deployment rule in section 8).
//   multiTermRequest: Loan terms becomes a multi-select and sends `loanTerms`
//     (work package 2). Off: one term, sent as `loanTerm`.
//   noFico: the No FICO box beside Est FICO, for a foreign national (work
//     package 4, D11). On: the calculator takes a foreign national without
//     creditScore.
//   entityVesting: Entity Vesting under Non-QM credit & investor (work
//     package 4, D12). On: the calculator takes `vesting`.
export const PPE_FEATURES = {
  multiTermRequest: false,
  noFico: true,
  entityVesting: true,
};

// First-lien Purchase defaults shown by the PPE form. Tests import the same
// object so the documented demo cannot drift away from the actual page state.
export const FIRST_LIEN_PURCHASE_DEFAULTS = {
  propertyValue: 500000,
  downPayment: 100000,
  loanAmount: 400000,
  secondaryLoanAmount: 0,
};

// These drive both the actual form dropdowns and the full loan-metrics test
// matrix. Keeping them here makes a newly added option fail coverage tests
// unless it is exercised with every supported purpose.
export const LOAN_PROGRAM_OPTIONS_BY_LIEN = {
  first: [
    { value: "conventional", label: "Conventional" },
    { value: "fha", label: "FHA" },
    { value: "va", label: "VA" },
    { value: "usda", label: "USDA" },
    { value: "nonqm", label: "Non-QM (Expanded)" },
  ],
  second: [
    { value: "heloc", label: "HELOC" },
    { value: "secondMortgage", label: "Second Mortgage" },
  ],
};

export const LOAN_PURPOSE_OPTIONS = [
  { value: "Purchase", label: "Purchase" },
  { value: "RateTermRefi", label: "Refinance" },
  { value: "CashOutRefi", label: "Cash Out" },
];

// Contract for the collapsible "Advanced & fee overrides" block.  The block
// is a first-lien-only part of the form; second-lien products expose their
// collateral/combined-loan inputs in the regular Loan section instead.
export const FIRST_LIEN_ADVANCED_FIELDS = [
  "appraisedValue",
  "totalLoanAmount",
  "secondaryLoanAmount",
  "cltv",
  "hcltv",
];

export function getAdvancedFieldsForScenario({ lienPosition, advancedOpen = true } = {}) {
  return lienPosition === "first" && advancedOpen ? FIRST_LIEN_ADVANCED_FIELDS : [];
}

// ppePricingUrl builds the POST target. view="engine" returns the raw engine
// result (common.Resp) that the reused pre-revamp results UI consumes; omit
// `view` for the LLM-first contract. customerid is intentionally NOT sent — the
// gateway takes it from the token so a client cannot spoof it.
export function ppePricingUrl(view = "engine") {
  return `${PPE_ENDPOINT}?view=${view}`;
}

// Defaults for fields the engine REQUIRES once a given loanProgram is selected
// (it 400s when they're absent). Single source of truth: the form seeds its
// inputs from these, and formatSearchParams backstops the wire request with them
// so a stored search saved before these existed still replays -- the history
// paths post the stored body directly, bypassing the form.
//
// fundingFee/guaranteeFee are PERCENTS. HELOC lineAmount remains distinct from
// drawAmount: the draw prices the active loan and drives CLTV, while the full
// line drives HCLTV and lineAmount - drawAmount drives Loan Balance pricing.
export const PROGRAM_FIELD_DEFAULTS = {
  vaFundingFee: 2.15, // first use, <5% down
  usdaGuaranteeFee: 1.0,
  helocLineAmount: 50000,
  helocDrawAmount: 50000,
  helocDrawTermMonths: 60,
  secondMortgageAmount: 50000,
  secondLienPrimaryMortgageAmount: 250000,
};

// LoanSifter-aligned borrower defaults used by both the form and the final
// request formatter (including replay paths that bypass the form).
export const BORROWER_DEFAULTS = {
  reserves: 24,
  propertiesFinanced: 1,
};

// Target price when none is usable (base 100: no points, no credit). Also the
// target for an old saved search that searched by rate (plan D17, D25).
export const DEFAULT_TARGET_PRICE = 100;

// HELOC repayment terms the calculator prices.
export const HELOC_LOAN_TERMS = ["fixed30", "fixed25", "fixed15"];

// ppeAuthHeaders returns the headers to send with every pricing request. The
// gateway reads the bearer token to authenticate and resolve customerid. Kept
// here so both fetch call sites stay in sync.
export function ppeAuthHeaders() {
  const token = sessionStorage.getItem("ZeitroA");
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = "Bearer " + token;
  return headers;
}

// The pricing API returns machine-readable failures as
// { error: { code, message, field, allowedValues, hint } }. Both pricing call
// sites must consume the body even for non-2xx responses; otherwise useful
// validation errors collapse to the browser's generic "Bad Request" text.
export async function parsePpeResponse(response) {
  const rawBody = await response.text();
  let payload = null;

  if (rawBody) {
    try {
      payload = JSON.parse(rawBody);
    } catch {
      // The authenticated gateway can return plain-text errors (for example,
      // an expired session). Preserve that text below instead of replacing it
      // with a JSON parse failure.
    }
  }

  if (!response.ok) {
    const apiError = payload?.error;
    const field = apiError?.field;
    const details = [];

    if (Array.isArray(apiError?.allowedValues) && apiError.allowedValues.length > 0) {
      details.push(`Allowed values: ${apiError.allowedValues.join(", ")}.`);
    }
    if (apiError?.hint) details.push(apiError.hint);

    const message = [
      apiError?.message || rawBody.trim() || response.statusText || `HTTP ${response.status}`,
      ...details,
    ].filter(Boolean).join(" ");
    const error = new Error(message);
    error.title = field ? `Invalid ${field}` : "Pricing request failed";
    error.code = apiError?.code;
    error.field = field;
    error.status = response.status;
    throw error;
  }

  if (payload === null) {
    const error = new Error("Pricing service returned an empty or invalid response.");
    error.title = "Pricing request failed";
    throw error;
  }

  return payload;
}
